import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import {
  ReportFilterState,
  toApiParams,
  applyExportDetailParams,
  exportDetailFilenameSuffix,
  ExportRequestEvent,
} from 'src/app/shared/components/report-filter-bar/report-filter-bar.component';

@Component({
  selector: 'app-advanced-analytics',
  templateUrl: './advanced-analytics.component.html',
  styleUrls: ['./advanced-analytics.component.scss']
})
export class AdvancedAnalyticsComponent implements OnInit {

  @Input() storeId: string = '';

  filterState: ReportFilterState = { period: 'this_month' };
  report: any = null;
  baBreakdown: any[] = [];
  comparison: any = { available: false };
  expandedBas: Record<string, boolean> = {};
  expandedVaregrupper: Record<string, boolean> = {};
  expandedProducts: Record<string, boolean> = {};
  productSalesCache: Record<string, { loading: boolean; error: boolean; rows: any[]; pagination: any; currentPage: number }> = {};
  loading: boolean = false;
  private loadSeq = 0;
  private readonly productSalesLimit = 20;
  private detectChangesPending = false;

  constructor(
    private ecommerceService: EcommerceService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (this.storeId) {
      // Initial load triggered by filter bar emit
    }
  }

  onFilterChange(filter: ReportFilterState): void {
    this.filterState = filter;
    this.loadReport();
  }

  loadReport(): void {
    if (!this.storeId) return;

    const params = toApiParams(this.filterState, { compare: 'true' });
    const seq = ++this.loadSeq;

    this.loading = true;
    this.report = null;
    this.baBreakdown = [];
    this.comparison = { available: false };
    this.expandedVaregrupper = {};
    this.expandedProducts = {};
    this.productSalesCache = {};
    this.spinner.show();
    this.ecommerceService.getStoreCategoryReport(this.storeId, params).subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq) return;
        this.report = this.unwrapResponse(res);
        this.baBreakdown = this.report?.baBreakdown || [];
        this.comparison = this.report?.comparison || { available: false };
        this.loading = false;
        this.spinner.hide();
      },
      error: (err: any) => {
        if (seq !== this.loadSeq) return;
        this.report = null;
        this.baBreakdown = [];
        this.comparison = { available: false };
        this.loading = false;
        this.spinner.hide();
        this.toastr.error(err.error?.response?.message || 'Failed to load category analytics');
      }
    });
  }

  onExport(event: ExportRequestEvent): void {
    this.spinner.show();
    const params = applyExportDetailParams(
      { format: event.format, ...toApiParams(this.filterState) },
      event.detailLevel
    );
    const suffix = exportDetailFilenameSuffix(event.detailLevel);
    this.ecommerceService.exportCategoryReport(this.storeId, params).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(
          blob,
          `category-report-${this.storeId}${suffix}-${new Date().toISOString().split('T')[0]}.${event.format}`
        );
        this.spinner.hide();
        this.toastr.success(this.translate.instant('KPI.ANALYTICS.EXPORT_SUCCESS'));
      },
      error: (err: any) => {
        this.spinner.hide();
        this.handleBlobError(err, 'Failed to export category report');
      }
    });
  }

  handleBlobError(error: any, fallback: string): void {
    if (error?.error instanceof Blob) {
      error.error.text().then((text: string) => {
        try {
          const parsed = JSON.parse(text);
          this.toastr.error(parsed?.response?.message || parsed?.message || fallback);
        } catch {
          this.toastr.error(text || fallback);
        }
      }).catch(() => this.toastr.error(fallback));
    } else {
      this.toastr.error(error?.error?.response?.message || error?.message || fallback);
    }
  }

  downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private scheduleDetectChanges(): void {
    if (this.detectChangesPending) {
      return;
    }
    this.detectChangesPending = true;
    setTimeout(() => {
      this.detectChangesPending = false;
      this.cdr.detectChanges();
    });
  }

  toggleBa(baId: string): void {
    this.expandedBas = { ...this.expandedBas, [baId]: !this.expandedBas[baId] };
    this.scheduleDetectChanges();
  }

  isExpanded(baId: string): boolean {
    return !!this.expandedBas[baId];
  }

  toggleVg(baId: string, categoryId: string): void {
    const key = `${baId}_${categoryId}`;
    this.expandedVaregrupper = { ...this.expandedVaregrupper, [key]: !this.expandedVaregrupper[key] };
    this.scheduleDetectChanges();
  }

  isVgExpanded(baId: string, categoryId: string): boolean {
    return !!this.expandedVaregrupper[`${baId}_${categoryId}`];
  }

  productKey(baId: string, categoryId: string, productId: string): string {
    return `${baId}_${categoryId}_${productId}`;
  }

  toggleProduct(baId: string, categoryId: string, productId: string): void {
    const key = this.productKey(baId, categoryId, productId);
    const willExpand = !this.expandedProducts[key];
    this.expandedProducts = { ...this.expandedProducts, [key]: willExpand };
    if (willExpand && !this.productSalesCache[key]) {
      this.loadProductSales(baId, categoryId, productId, 1);
    }
    this.scheduleDetectChanges();
  }

  isProductExpanded(baId: string, categoryId: string, productId: string): boolean {
    return !!this.expandedProducts[this.productKey(baId, categoryId, productId)];
  }

  loadProductSales(baId: string, categoryId: string, productId: string, page: number = 1): void {
    const key = this.productKey(baId, categoryId, productId);
    const isLoadMore = page > 1;

    if (!isLoadMore) {
      this.productSalesCache = {
        ...this.productSalesCache,
        [key]: { loading: true, error: false, rows: [], pagination: null, currentPage: 1 }
      };
    } else {
      const existing = this.productSalesCache[key];
      if (!existing || existing.loading) return;
      this.productSalesCache = {
        ...this.productSalesCache,
        [key]: { ...existing, loading: true }
      };
    }
    this.scheduleDetectChanges();

    const params = {
      ...toApiParams(this.filterState),
      page,
      limit: this.productSalesLimit
    };

    this.ecommerceService.getProductSaleEvents(this.storeId, productId, params).subscribe({
      next: (res: any) => {
        const data = this.unwrapResponse(res);
        const prev = this.productSalesCache[key];
        this.productSalesCache = {
          ...this.productSalesCache,
          [key]: {
            loading: false,
            error: false,
            rows: isLoadMore ? [...(prev?.rows || []), ...(data.sales || [])] : (data.sales || []),
            pagination: data.pagination,
            currentPage: data.pagination?.currentPage ?? page
          }
        };
        this.scheduleDetectChanges();
      },
      error: (err: any) => {
        if (!isLoadMore) {
          this.productSalesCache = {
            ...this.productSalesCache,
            [key]: { loading: false, error: true, rows: [], pagination: null, currentPage: 1 }
          };
        } else {
          const existing = this.productSalesCache[key];
          this.productSalesCache = {
            ...this.productSalesCache,
            [key]: { ...existing, loading: false }
          };
        }
        this.scheduleDetectChanges();
        this.toastr.error(err.error?.response?.message || this.translate.instant('KPI.ANALYTICS.LOAD_SALES_ERROR'));
      }
    });
  }

  loadMoreProductSales(baId: string, categoryId: string, productId: string): void {
    const key = this.productKey(baId, categoryId, productId);
    const cache = this.productSalesCache[key];
    if (!cache || cache.loading) return;
    if (!cache.pagination || cache.currentPage >= cache.pagination.totalPages) return;
    this.loadProductSales(baId, categoryId, productId, cache.currentPage + 1);
  }

  formatOrderDate(orderDate: string): string {
    if (!orderDate) return '-';
    return new Date(orderDate).toLocaleDateString('da-DK');
  }

  formatOrderTime(orderDate: string): string {
    if (!orderDate) return '-';
    return new Date(orderDate).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
  }

  unwrapResponse(res: any): any {
    if (res?.response?.success && res.response.data !== undefined) return res.response.data;
    if (res?.code === 200 && res.data !== undefined) return res.data;
    return res;
  }

  formatCurrency(value: number): string {
    if (value == null) return 'kr 0.00';
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(value);
  }

  formatNullableCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    return this.formatCurrency(value);
  }

  formatMoney(value: number | null | undefined, nullable = false): string {
    if (nullable && (value === null || value === undefined)) return '-';
    return this.formatCurrency(value ?? 0);
  }

  formatTaxRate(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return `${value.toFixed(2)}%`;
  }

  formatNumber(value: number): string {
    if (value == null) return '0';
    return new Intl.NumberFormat('da-DK').format(value);
  }

  formatDelta(deltaPct: number | null | undefined): string {
    if (deltaPct == null || isNaN(deltaPct)) return '';
    const sign = deltaPct >= 0 ? '+' : '';
    return `${sign}${deltaPct.toFixed(1)}%`;
  }

  deltaClass(deltaPct: number | null | undefined): string {
    if (deltaPct == null || isNaN(deltaPct)) return 'text-muted';
    if (deltaPct > 0) return 'text-success';
    if (deltaPct < 0) return 'text-danger';
    return 'text-muted';
  }
}
